import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Flex, ModalBody, ModalFooter, ModalHeader, Table, Td, Text, Thead, Tr } from "@chakra-ui/react";
import { Tbody } from "@familyzone/component-library";

import { ClasswizeModal } from "@/components/ui/Modal";
import { TableContainer, Th } from "@/components/ui/Table";
import { useToast } from "@/components/ui/Toast";
import { useTranslation } from "react-i18next";
import { filterStudentClaimsForOtherClasses, useStudentClaimsQuery } from "@/query/studentClaimsQuery";

export type StudentClaimsModalProps = {
	isOpen: boolean;
	onClose: () => void;
	classroomStableId: string;
	/** Called with the selected student QIDs when the user confirms. */
	onClaimStudents: (studentIds: string[]) => Promise<void>;
};

/**
 * Student management dialog: lists students managed by another class, with selection and claim action.
 * Layout and styling follow Combine Classes modal patterns (ClasswizeModal, TableContainer, qoriaStriped).
 */
export const StudentClaimsModal: React.FC<StudentClaimsModalProps> = ({ isOpen, onClose, classroomStableId, onClaimStudents }) => {
	const { t } = useTranslation();
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const studentClaimsQuery = useStudentClaimsQuery({ classroomStableId: classroomStableId });
	const claims = useMemo(
		() => filterStudentClaimsForOtherClasses(studentClaimsQuery.data ?? [], classroomStableId),
		[studentClaimsQuery.data, classroomStableId]
	);
	useEffect(() => {
		if (!isOpen) {
			return;
		}
		if (claims.length > 0) {
			setSelectedIds(new Set(claims.map((c) => c.studentId)));
		}
	}, [isOpen, claims]);

	const selectedCount = selectedIds.size;
	const allIds = useMemo(() => claims.map((c) => c.studentId), [claims]);
	const allSelected = claims.length > 0 && selectedCount === claims.length;
	const someSelected = selectedCount > 0 && !allSelected;
	const toast = useToast();

	const toggleRow = useCallback((studentId: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(studentId)) {
				next.delete(studentId);
			} else {
				next.add(studentId);
			}
			return next;
		});
	}, []);

	const toggleSelectAll = useCallback(() => {
		setSelectedIds(() => {
			if (allSelected) {
				return new Set();
			}
			return new Set(allIds);
		});
	}, [allSelected, allIds]);

	const handleClaimClick = useCallback(async () => {
		if (selectedIds.size === 0) {
			return;
		}
		await onClaimStudents([...selectedIds])
			.catch((error) => {
				toast.errorToast({
					title: t("classroomDetails.failedToClaimStudents"),
					description: error.message,
				});
			})
			.finally(() => {
				onClose();
			});
	}, [onClaimStudents, onClose, selectedIds, toast, t]);

	return (
		<ClasswizeModal
			isOpen={isOpen}
			title={t("classroomDetails.studentManagement")}
			onClose={onClose}
			size="5xl"
			dataTestId="student-claims-modal"
		>
			<ModalHeader pt={0}>
				<Flex direction="column" gap={1} pr={8}>
					<Text color="#051839" fontFamily="Roboto" fontSize="14px" fontWeight={400} lineHeight="20px">
						{t("classroomDetails.claimsInstructionPrimary")} {t("classroomDetails.claimsInstructionSecondary")}
					</Text>
				</Flex>
			</ModalHeader>

			<ModalBody>
				{claims.length === 0 ? (
					<Text color="gray.600" fontFamily="Roboto" fontSize="14px">
						{t("classroomDetails.noStudentClaims")}
					</Text>
				) : (
					<TableContainer>
						<Table variant="qoriaStriped">
							<Thead>
								<Tr>
									<Th width="48px">
										<Checkbox
											colorScheme="blue"
											isChecked={allSelected}
											isIndeterminate={someSelected}
											onChange={toggleSelectAll}
											aria-label={t("classroomDetails.selectAllStudents")}
											data-testid="student-claims-select-all"
										/>
									</Th>
									<Th>{t("classroomDetails.student")}</Th>
									<Th>{t("classroomDetails.class")}</Th>
									<Th>{t("classroomDetails.teacher")}</Th>
								</Tr>
							</Thead>
							<Tbody>
								{claims.map((row) => (
									<Tr key={`${row.studentId}-${row.classId}`}>
										<Td>
											<Checkbox
												colorScheme="blue"
												isChecked={selectedIds.has(row.studentId)}
												onChange={() => toggleRow(row.studentId)}
												aria-label={t("classroomDetails.selectStudent", { name: row.studentName })}
												data-testid={`student-claims-row-${row.studentId}`}
											/>
										</Td>
										<Td>
											<Text fontSize="md" fontFamily="Roboto">
												{row.studentName}
											</Text>
										</Td>
										<Td>
											<Text fontSize="md" fontFamily="Roboto">
												{row.className}
											</Text>
										</Td>
										<Td>
											<Text fontSize="md" fontFamily="Roboto">
												{row.teacherName}
											</Text>
										</Td>
									</Tr>
								))}
							</Tbody>
						</Table>
					</TableContainer>
				)}
			</ModalBody>

			{claims.length > 0 && (
				<ModalFooter>
					<Flex width="100%" alignItems="center">
						<Flex grow={1} />
						<Flex>
							<Button
								colorScheme="blue"
								borderRadius="md"
								isDisabled={selectedCount === 0}
								onClick={() => void handleClaimClick()}
								data-testid="student-claims-modal-test-btn"
								id="student-claims-modal-btn"
							>
								{t("classroomDetails.claimStudentCount", { count: selectedCount })}
							</Button>
						</Flex>
					</Flex>
				</ModalFooter>
			)}
		</ClasswizeModal>
	);
};
